import { useCallback, useMemo, useState } from 'react';
import { Box, Checkbox, IconButton, Tooltip } from '@mui/material';
import { darken, lighten, Theme } from '@mui/material/styles';
import { DataGridPro, useGridApiRef, gridVisibleSortedRowIdsSelector, gridClasses } from '@mui/x-data-grid-pro';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DataGridToolBar from '../DataGridToolBar';
import { GridTreeDataGroupingCell } from './GridTreeDataGroupingCell';
import { OsvColumnsPanel } from './OsvColumnsPanel';
import { StateFooter } from './StateFooter';
import { tokens, useTranslation } from '../../utils/i18n';

// OSI-808: префикс id закреплённых строк итогов. Вынесен в экспорт, чтобы потребители
// формировали id по одному правилу и гарантированно не пересекались с id строк данных.
// При совпадении id MUI молча выбрасывает настоящую строку данных из ids
// (@mui/x-data-grid-pro 5.17.26, hooks/features/rowPinning/useGridRowPinningPreProcessors.js:115-121).
export const OSV_TOTAL_ROW_ID_PREFIX = '__osv_total__';

const isOsvTotalRowId = (id: unknown): boolean =>
  typeof id === 'string' && id.startsWith(OSV_TOTAL_ROW_ID_PREFIX);

// OSI-808: experimentalFeatures включаем БЕЗУСЛОВНО и один раз, т.к. rowPinningStateInitializer
// читает флаг только на маунте (hooks/features/rowPinning/useGridRowPinning.js): если включить
// флаг позже, когда totalRows придёт с сервера, инициализация состояния уже не повторится.
// Аддитивность не нарушается: при totalRows === undefined pinnedRows.bottom тоже undefined,
// ни одной закреплённой строки не появляется, экраны ведут себя как раньше.
const OSV_EXPERIMENTAL_FEATURES = { rowPinning: true } as const;

// OSI-808: КОНТРАКТ СТРОКИ ИТОГОВ (элемента массива totalRows) — им пользуются виджеты ОСВ:
//  - id: string — обязательно с префиксом OSV_TOTAL_ROW_ID_PREFIX
//    (например `${OSV_TOTAL_ROW_ID_PREFIX}grand`, `${OSV_TOTAL_ROW_ID_PREFIX}cat_0`);
//  - flat: any[] и period: any[] — ОБА, одноэлементные массивы с одной и той же подписью
//    («ИТОГО» либо название категории). Первая текстовая колонка различается по экранам:
//    `flat` на «ОСВ ОСИ» и «Сальдо ОСИ», `period` на «ОСВ по помещению» и «Сальдо квартиры»;
//    обе колонки имеют valueGetter, берущий последний элемент массива, поэтому лишнее поле
//    на конкретном экране просто игнорируется;
//  - totalBegin, totalDebet, totalFixes, totalKredit, totalFine, totalEnd — все шесть, числом
//    (не undefined), иначе в ячейке будет пусто;
//  - поле level ставить НЕЛЬЗЯ: закреплённые строки вне дерева, level используется только
//    для класса отступа row-child--level-N у строк данных;
//  - путь дерева не нужен: getTreeDataPath для закреплённых строк не вызывается
//    (rowTreeCreation отрабатывает раньше hydrateRows, а useGridTreeDataPreProcessors
//    итерирует по params.ids, куда pinned-строки не попадают);
//  - порядок: строки рисуются сверху вниз в порядке массива — сначала «ИТОГО», затем категории.

// OSI-808: переключатель категорий в служебной ячейке строки «ИТОГО».
// Tooltip КОНТРОЛИРУЕМЫЙ и закрывается по клику. Причина — подсказка «зависала» после клика:
// строка «ИТОГО» первая в блоке pinnedRows.bottom, а этот блок — position: sticky; bottom: 0
// (DataGridProVirtualScroller.js, VirtualScrollerPinnedRows). При раскрытии/сворачивании
// высота блока меняется, и кнопка уезжает вверх/вниз из-под НЕПОДВИЖНОГО курсора.
// Неконтролируемый Tooltip закрывается только по mouseleave/blur/Escape: mouseleave кнопка
// при таком сдвиге не получает (курсор не двигался — уехал сам элемент), blur тоже нет
// (фокус после клика остаётся на кнопке). Поппер при этом не перепозиционируется и висит
// на старом месте, пока пользователь не наведёт на него (Tooltip интерактивный) и не уведёт.
// Закрывая подсказку сами в onClick, мы не зависим от того, дойдёт ли mouseleave.
// Компонент объявлен на уровне модуля (а не внутри renderCell), чтобы его тип был стабильным
// между рендерами и ячейка не перемонтировалась; состояние open локальное — наведение
// перерисовывает только эту ячейку, а не весь DataGridPro.
interface OsvTotalsToggleCellProps {
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
}

const OsvTotalsToggleCell: React.FC<OsvTotalsToggleCellProps> = ({ label, isExpanded, onToggle }) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  return (
    <Tooltip
      title={label}
      open={isTooltipOpen}
      onOpen={() => setIsTooltipOpen(true)}
      onClose={() => setIsTooltipOpen(false)}
    >
      {/* stopPropagation — как в handleClick у GridTreeDataGroupingCell: клик не должен
          уходить в грид и переводить фокус на ячейку закреплённой строки */}
      <IconButton
        aria-label={label}
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          // OSI-808: закрываем подсказку ДО переключения — см. комментарий над компонентом
          setIsTooltipOpen(false);
          onToggle();
        }}
      >
        {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
      </IconButton>
    </Tooltip>
  );
};

interface Props {
  columns: any[];
  rows: any[];
  totals: any[];
  rowCount: number;
  getTreeDataPath: (row: any) => any;
  isLoading?: boolean;
  // OSI-788: ОПЦИОНАЛЬНОЕ управление видимостью колонок. Грид общий для четырёх экранов
  // (ОСВ ОСИ, ОСВ по квартире, Сальдо ОСИ, Сальдо квартиры) — там, где пропсы не переданы,
  // они уходят в DataGridPro как undefined, т.е. состояние видимости остаётся полностью
  // внутренним (неконтролируемым), и поведение экранов не меняется.
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: (model: Record<string, boolean>) => void;
  // OSI-808: ОПЦИОНАЛЬНЫЕ строки итогов, закреплённые снизу таблицы (pinnedRows.bottom).
  // Формат элемента — см. «КОНТРАКТ СТРОКИ ИТОГОВ» выше. При undefined ни одной закреплённой
  // строки не рендерится, т.е. поведение всех четырёх экранов-потребителей не меняется.
  totalRows?: any[];
}

export const OsvGrid: React.FC<Props> = ({
  columns,
  rows,
  totals,
  rowCount,
  getTreeDataPath,
  isLoading = false,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  totalRows
}) => {
  const apiRef = useGridApiRef();

  // OSI-808: блок закреплённых итогов сворачиваемый, по умолчанию СВЁРНУТ. Причина —
  // высота pinned-строк ничем не ограничена: VirtualScrollerPinnedRows это sticky-блок
  // с непрозрачным фоном и inline height = сумме высот строк, а из размеров области
  // прокрутки эта высота не вычитается (useGridVirtualScroller берёт clientHeight целиком,
  // contentSize считается только по currentPage.rows, куда pinned-строки не входят).
  // При density="compact" (36px) и 10 категориях блок в 360px съедал весь контейнер 60vh
  // на ноутбуке — строк данных было не видно.
  const [isTotalsExpanded, setIsTotalsExpanded] = useState(false);

  // OSI-808: разворачивать нечего, если категорий нет (пусто или только строка «ИТОГО»).
  const hasTotalCategories = Array.isArray(totalRows) && totalRows.length > 1;

  // OSI-808: есть ли закреплённая строка «ИТОГО» (нужно для иконки-переключателя ниже).
  // Подвал (StateFooter) дублировал бы её теми же шестью числами — когда он скрывается,
  // см. isFooterReplacedByTotalRows.
  const hasTotalRows = Array.isArray(totalRows) && totalRows.length > 0;

  // OSI-808: подвал скрываем по факту ПЕРЕДАЧИ пропа totalRows, а не по его непустоте.
  // Раньше условием было hasTotalRows, и на «ОСВ ОСИ» до первой загрузки (osvTotalRows = [],
  // totalsOsv = [] в ViewModel) под спиннером мелькал старый подвал с нулями, а после
  // загрузки его сменяла строка «ИТОГО». Экраны, передающие totalRows («ОСВ ОСИ», «Сальдо
  // ОСИ»), подвал не используют вовсе: пустой totalRows там бывает только без строк услуг
  // (level 1), а тогда и все суммы подвала нулевые (getOsvRows суммирует только их поля) —
  // показывать нечего. Экраны без totalRows («Помещения» у председателя и жителя) получают
  // подвал как раньше.
  const isFooterReplacedByTotalRows = totalRows !== undefined;

  // OSI-808: id grand-строки («ИТОГО») для иконки-переключателя в служебной колонке.
  // Опознаём её как ПЕРВЫЙ элемент totalRows, а не по полному id `${PREFIX}grand`:
  // суффикс 'grand' формируется в helpers/osvTotalRows.ts (чужой файл), и вторая копия
  // этой строковой константы здесь молча разъехалась бы при его переименовании, выключив
  // иконку без единой ошибки. Порядок же зафиксирован в «КОНТРАКТЕ СТРОКИ ИТОГОВ» выше
  // («сначала ИТОГО, затем категории») — на него опирается и сворачивание (slice(0, 1)),
  // так что оба места ломались бы одновременно и заметно.
  const grandTotalRowId = hasTotalRows ? totalRows?.[0]?.id : undefined;

  // OSI-808: мемоизация обязательна — новый объект на каждый рендер заставлял бы
  // useGridRowPinning дёргать unstable_setPinnedRows в useEffect по props.pinnedRows.
  // В свёрнутом виде показываем только первый элемент — по контракту totalRows это
  // строка «ИТОГО» (grand), категории идут после неё.
  const pinnedRows = useMemo(
    () => ({ bottom: !totalRows || isTotalsExpanded ? totalRows : totalRows.slice(0, 1) }),
    [totalRows, isTotalsExpanded]
  );

  // OSI-808: обработчик переключения уезжает в панель над таблицей через componentsProps.toolbar;
  // useCallback — чтобы ссылка не менялась на каждый рендер.
  const handleToggleTotals = useCallback(() => setIsTotalsExpanded((prev) => !prev), []);

  const { getGridLocale, translateToken: tt } = useTranslation();

  // OSI-808: одна подпись на оба переключателя (иконка в строке «ИТОГО» и кнопка в панели) —
  // используется и как tooltip, и как aria-label иконки.
  const toggleTotalsLabel = tt(
    isTotalsExpanded ? tokens.osiOSV.table.footer.hideCategories : tokens.osiOSV.table.footer.showCategories
  );

  const handleExpandAllClick = () => {
    const rowIds = gridVisibleSortedRowIdsSelector(apiRef.current.state);
    if (rows.length > 1) {
      rowIds.forEach((rowId) =>
        apiRef.current.setRowChildrenExpansion(rowId, !apiRef.current.getRowNode(rowId)?.childrenExpanded)
      );
    }
  };

  const isExpanded = () => {
    const rowIds = gridVisibleSortedRowIdsSelector(apiRef.current.state);

    if (rows.length > 1) {
      const expanded = rowIds.reduce((count, rowId) => {
        if (apiRef.current.getRowNode(rowId)?.childrenExpanded) {
          return count + 1;
        }
        return count;
      }, 0);
      return expanded === rowCount || expanded === rows?.length;
    }
    return false;
  };

  const groupingColDef = {
    headerName: '',
    align: 'left',
    width: 60,
    renderHeader: () => (
      <Box sx={{ ml: -1 }}>
        <Checkbox checked={isExpanded()} onChange={handleExpandAllClick} />
      </Box>
    ),
    // OSI-808: у grand-строки итогов служебная ячейка была пустой (закреплённые строки вне
    // дерева, потомков у них нет). Рисуем в ней тот же переключатель, что и в панели над
    // таблицей: у строк помещений здесь стрелка «раскрыть услуги», значит у «ИТОГО» логично
    // ждать стрелку «раскрыть категории». Состояние общее (isTotalsExpanded), поэтому
    // оба переключателя всегда синхронны. Иконки и IconButton size="small" — ровно как
    // в GridTreeDataGroupingCell, чтобы ячейка выглядела родной; depth у закреплённых строк
    // равен 0, так что отступа нет и иконка встаёт по одной линии со стрелками верхнего уровня.
    // Клавиатурную навигацию по дереву (handleKeyDown/setCellFocus) намеренно не переносим —
    // она нужна для обхода дерева, а здесь достаточно доступности самого IconButton.
    renderCell: (params: any) => {
      if (hasTotalCategories && params.id === grandTotalRowId) {
        return (
          <OsvTotalsToggleCell label={toggleTotalsLabel} isExpanded={isTotalsExpanded} onToggle={handleToggleTotals} />
        );
      }
      return <GridTreeDataGroupingCell {...params} />;
    }
  };

  return (
    <DataGridPro
      localeText={getGridLocale()}
      treeData
      apiRef={apiRef}
      getTreeDataPath={getTreeDataPath}
      groupingColDef={groupingColDef}
      // OSI-808: у закреплённых строк итогов поля level нет и быть не должно (они вне дерева),
      // иначе получался бы мусорный класс "row-child--level-undefined".
      getRowClassName={(params) => {
        if (isOsvTotalRowId(params.id)) {
          // OSI-808: строку «ИТОГО» помечаем отдельным классом — по нему она прилипает к верху
          // прокручиваемого блока итогов (см. sx ниже). Опознаём по grandTotalRowId, а не по
          // суффиксу id — по той же причине, что и иконку-переключатель.
          return params.id === grandTotalRowId ? 'osv-total-row osv-total-row--grand' : 'osv-total-row';
        }
        const level = params.getValue(params.id, 'level');
        return level == null ? '' : `row-child--level-${level}`;
      }}
      // OSI-808: закреплённые снизу строки итогов. Рендерятся внутри виртуального скроллера,
      // поэтому выравнивание по колонкам, синхронный горизонтальный скролл и учёт скрытых
      // колонок работают штатно, без дублирования разметки.
      experimentalFeatures={OSV_EXPERIMENTAL_FEATURES}
      pinnedRows={pinnedRows}
      // OSI-808: подсветка строк итогов. Стили задаём через sx самого DataGridPro, а НЕ через
      // обёртку <Box>: обёртка без height: '100%' схлопнула бы грид, т.к. высоту задают
      // потребители на своём внешнем Box (например height: '60vh').
      // theme берём колбэком верхнего уровня sx и типизируем ЯВНО: контекстная типизация
      // параметра здесь не срабатывает (generic-сигнатура DataGridPro), и без аннотации
      // tsc ругается TS7006 «Parameter 'theme' implicitly has an any type».
      sx={(theme: Theme) => ({
        '& .osv-total-row': {
          fontWeight: 700,
          bgcolor:
            theme.palette.mode === 'dark'
              ? darken(theme.palette.info.main, 0.7)
              : lighten(theme.palette.info.main, 0.85)
        },
        '& .osv-total-row:hover': {
          bgcolor:
            theme.palette.mode === 'dark'
              ? darken(theme.palette.info.main, 0.65)
              : lighten(theme.palette.info.main, 0.8)
        },
        // OSI-808: страховка по высоте блока закреплённых строк. Библиотека высоту не
        // ограничивает (calculatePinnedRowsHeight — голый reduce без clamp), inline height
        // на контейнере перебивается max-height без !important. overflow-x: hidden задан
        // ЯВНО: иначе при overflow-y: auto браузер по спецификации вычислит overflow-x: auto
        // и появится вторая горизонтальная полоса прокрутки, разъезжающаяся с шапкой.
        // Собственный overflow на sticky-элементе его не ломает (ломал бы overflow у предка).
        [`& .${gridClasses['pinnedRows--bottom']}`]: {
          maxHeight: '50%',
          overflowY: 'auto',
          overflowX: 'hidden'
        },
        // OSI-808: при прокрутке раскрытого блока итогов строка «ИТОГО» остаётся на месте,
        // а категории уезжают под неё. Прокручивается сам контейнер pinnedRows--bottom
        // (overflowY: auto выше) — он и есть ближайший скролл-контейнер для sticky.
        // DOM блока (DataGridProVirtualScroller.js): VirtualScrollerPinnedRows (этот контейнер)
        // → VirtualScrollerPinnedRowsRenderZone (position: absolute + transform translateX для
        // синхронного горизонтального скролла, overflow не задан) → строки, все подряд, без
        // виртуализации; первой идёт «ИТОГО». Родитель строки — RenderZone высотой во все
        // строки блока, поэтому sticky-строке есть где «ехать» до самого конца прокрутки;
        // transform предка sticky не ломает (ломал бы только overflow между ними — его нет).
        // По горизонтали sticky не действует: строка сдвигается вместе с RenderZone, колонки
        // совпадают с таблицей как раньше. Фон строки непрозрачный (lighten/darken выше),
        // zIndex: 1 — чтобы категории проходили ПОД ней. В свёрнутом виде блок из одной строки
        // не прокручивается, и sticky ни на что не влияет. Закреплённых колонок в ОСВ нет;
        // при их появлении (отдельная секция VirtualScrollerPinnedColumns) правило надо
        // перепроверить.
        [`& .${gridClasses['pinnedRows--bottom']} .osv-total-row--grand`]: {
          position: 'sticky',
          top: 0,
          zIndex: 1
        }
      })}
      rows={rows}
      columns={columns}
      // OSI-788: пропсы опциональны — при undefined DataGridPro управляет видимостью сам (как раньше)
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      disableChildrenFiltering
      density="compact"
      disableSelectionOnClick
      // OSI-808: к прежнему условию (!totals — подвала нет, когда итогов не передали вовсе)
      // добавлено isFooterReplacedByTotalRows (см. комментарий у переменной: раньше здесь
      // было hasTotalRows, из-за чего подвал мелькал при загрузке). Именно hideFooter, а не
      // подмена components.Footer: GridFooterPlaceholder при hideFooter возвращает null
      // целиком (components/base/GridFooterPlaceholder.js), т.е. под таблицей не остаётся
      // пустого div с рамкой.
      hideFooter={!totals || isFooterReplacedByTotalRows}
      loading={isLoading}
      components={{
        Toolbar: DataGridToolBar,
        Footer: StateFooter,
        // OSI-808: своя панель выбора столбцов вместо штатной GridColumnsPanel — прячет из списка
        // служебное поле группировки дерева __tree_data_group__ (см. комментарий в OsvColumnsPanel.tsx).
        ColumnsPanel: OsvColumnsPanel
      }}
      componentsProps={{
        footer: { totals },
        // OSI-808: состояние и обработчик сворачивания категорий уходят в ПАНЕЛЬ НАД таблицей
        // (подвал на этих экранах скрыт как дублирующий). DataGridToolBar общий для нескольких
        // гридов проекта, поэтому пропсы опциональны: где их не передают — панель прежняя.
        toolbar: {
          hasTotalCategories,
          isTotalsExpanded,
          onToggleTotals: handleToggleTotals
        }
      }}
    />
  );
};
